import { useState, useEffect, useRef } from "react";
import Topbar from "../components/layout/Topbar.jsx";
import CompanySelector from "../components/ui/CompanySelector.jsx";
import { Modal, EmptyState } from "../components/ui/index.jsx";
import ToastContainer from "../config/ToastContainer.jsx";
import { useToast } from "../config/useToast.js";
import { getCredentials } from "../services/credentialService.js";
import {
  getOverview,
  registerNas, updateNas, deleteNas, syncNasNow, syncOneNas,
  registerGateway, updateGateway, deleteGateway, syncGatewaysNow, syncOneGateway,
  discoverUnifiHosts,
} from "../services/monitoringService.js";

// ─────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────

function timeAgo(iso) {
  if (!iso) return "jamais";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)    return `il y a ${diff} sec`;
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}

/** Badge de statut 3 niveaux (OK/WARNING/CRITICAL) — réutilise les classes
 *  badge-prio déjà définies globalement (bp-low/bp-high/bp-critical). */
function HealthBadge({ status }) {
  const map = {
    OK:       ["bp-low",      "OK"],
    WARNING:  ["bp-high",     "Avertissement"],
    CRITICAL: ["bp-critical", "Critique"],
  };
  const [cls, label] = map[status] || ["bp-low", status || "—"];
  return (
    <span className={`badge-prio ${cls}`}>
      <i className="bi bi-circle-fill" style={{ fontSize: 6 }} />{label}
    </span>
  );
}

// ─────────────────────────────────────────────
//  MODAL — AJOUTER UN NAS
// ─────────────────────────────────────────────

function AddNasModal({ editing, onClose, onSaved, toast }) {
  const isEdit = !!editing;
  const [company, setCompany]   = useState(isEdit ? { id: editing.companyId, name: editing.companyName } : null);
  const [name, setName]         = useState(editing?.name ?? "");
  const [host, setHost]         = useState(editing?.quickConnectHost ?? "");
  const [credentials, setCredentials] = useState([]);
  const [credentialId, setCredentialId] = useState(editing?.credentialId ?? "");
  const [loadingCreds, setLoadingCreds] = useState(false);
  const [saving, setSaving]     = useState(false);

  // Charge les credentials de type NAS de l'entreprise choisie. En édition,
  // ne réinitialise pas credentialId au premier rendu (même entreprise que
  // l'existant) — seulement si l'admin change explicitement d'entreprise.
  const firstRun = useRef(true);
  useEffect(() => {
    if (!company) { setCredentials([]); setCredentialId(""); return; }
    if (firstRun.current) {
      firstRun.current = false;
    } else if (!isEdit || company.id !== editing.companyId) {
      setCredentialId("");
    }
    setLoadingCreds(true);
    getCredentials(company.id, { type: "NAS" })
      .then(res => setCredentials(res?.data ?? []))
      .catch(() => setCredentials([]))
      .finally(() => setLoadingCreds(false));
  }, [company]);

  const canSave = company && name.trim() && host.trim() && credentialId && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        companyId: company.id,
        name: name.trim(),
        quickConnectHost: host.trim(),
        credentialId,
      };
      if (isEdit) {
        await updateNas(editing.id, payload);
        toast.success("NAS mis à jour");
      } else {
        await registerNas(payload);
        toast.success("NAS enregistré");
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? `Modifier "${editing.name}"` : "Ajouter un NAS Synology"}
      onClose={onClose}
      footer={
        <>
          <button className="btn-itm btn-itm-outline" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-itm btn-itm-primary" onClick={handleSave} disabled={!canSave}>
            {saving ? <><i className="bi bi-arrow-repeat spin me-1"/>Enregistrement…</> : (isEdit ? "Sauvegarder" : "Enregistrer")}
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="form-group">
          <label className="itm-label">Entreprise cliente</label>
          <CompanySelector selectedId={company?.id} onChange={setCompany} />
        </div>

        <div className="form-group">
          <label className="itm-label">Nom (affiché dans la liste)</label>
          <input className="itm-input" value={name} onChange={e => setName(e.target.value)}
            placeholder="Ex: NAS Clinique X" />
        </div>

        <div className="form-group">
          <label className="itm-label">Nom d'hôte QuickConnect / DDNS</label>
          <input className="itm-input" value={host} onChange={e => setHost(e.target.value)}
            placeholder="xxxxx.synology.me" />
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>
            Trouvable dans DSM : Panneau de configuration → Accès externe → DDNS.
          </div>
        </div>

        <div className="form-group">
          <label className="itm-label">Identifiants (coffre)</label>
          {!company ? (
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>Choisis d'abord une entreprise.</div>
          ) : loadingCreds ? (
            <div style={{ fontSize: 12, color: "var(--text-3)" }}><i className="bi bi-arrow-repeat spin me-1"/>Chargement…</div>
          ) : credentials.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--danger)" }}>
              Aucune credential de type NAS pour cette entreprise — crée-la d'abord dans le coffre (Mots de passe → type NAS).
            </div>
          ) : (
            <select className="itm-input" value={credentialId} onChange={e => setCredentialId(e.target.value)}>
              <option value="">— Choisir —</option>
              {credentials.map(c => (
                <option key={c.id} value={c.id}>{c.name} ({c.username})</option>
              ))}
            </select>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────
//  MODAL — AJOUTER UNE PASSERELLE UNIFI
// ─────────────────────────────────────────────

function AddGatewayModal({ editing, onClose, onSaved, toast }) {
  const isEdit = !!editing;
  const [company, setCompany]   = useState(isEdit ? { id: editing.companyId, name: editing.companyName } : null);
  const [name, setName]         = useState(editing?.name ?? "");
  const [hostId, setHostId]     = useState(editing?.unifiHostId ?? "");
  const [hosts, setHosts]       = useState(null); // null = pas encore cherché
  const [discovering, setDiscovering] = useState(false);
  const [saving, setSaving]     = useState(false);

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const list = await discoverUnifiHosts();
      setHosts(list || []);
    } catch (err) {
      toast.error(err.message || "Erreur lors de la détection");
      setHosts([]);
    } finally {
      setDiscovering(false);
    }
  };

  const pickHost = (h) => {
    setHostId(h.id);
    if (!name.trim()) setName(h.name || "");
  };

  const canSave = company && name.trim() && hostId.trim() && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = { companyId: company.id, name: name.trim(), unifiHostId: hostId.trim() };
      if (isEdit) {
        await updateGateway(editing.id, payload);
        toast.success("Passerelle mise à jour");
      } else {
        await registerGateway(payload);
        toast.success("Passerelle enregistrée");
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? `Modifier "${editing.name}"` : "Ajouter une passerelle UniFi"}
      onClose={onClose}
      footer={
        <>
          <button className="btn-itm btn-itm-outline" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-itm btn-itm-primary" onClick={handleSave} disabled={!canSave}>
            {saving ? <><i className="bi bi-arrow-repeat spin me-1"/>Enregistrement…</> : (isEdit ? "Sauvegarder" : "Enregistrer")}
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="form-group">
          <label className="itm-label">Entreprise cliente</label>
          <CompanySelector selectedId={company?.id} onChange={setCompany} />
        </div>

        <div className="form-group">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <label className="itm-label" style={{ marginBottom: 0 }}>Site UniFi (Site Manager)</label>
            <button className="btn-itm btn-itm-outline" style={{ padding: "4px 10px", fontSize: 11 }}
              onClick={handleDiscover} disabled={discovering}>
              <i className={`bi bi-search ${discovering ? "spin" : ""} me-1`}/>
              {discovering ? "Recherche…" : "Détecter les sites"}
            </button>
          </div>

          {hosts === null ? (
            <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 6 }}>
              Clique sur "Détecter les sites" pour lister les hosts vus par ton compte unifi.ui.com.
            </div>
          ) : hosts.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 6 }}>Aucun host détecté.</div>
          ) : (
            <div style={{ marginTop: 8, maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
              {hosts.map(h => (
                <button
                  key={h.id}
                  onClick={() => pickHost(h)}
                  className="btn-itm btn-itm-outline"
                  style={{
                    justifyContent: "flex-start", textAlign: "left", fontSize: 12,
                    borderColor: hostId === h.id ? "var(--accent)" : undefined,
                    background: hostId === h.id ? "var(--accent-dim)" : undefined,
                  }}
                >
                  <i className={`bi bi-circle-fill me-2`} style={{ fontSize: 7, color: h.online ? "var(--success)" : "var(--danger)" }}/>
                  <span style={{ fontWeight: 600 }}>{h.name || "(sans nom)"}</span>
                  <span style={{ marginLeft: 8, color: "var(--text-3)" }}>{h.ipAddress}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="itm-label">Nom (affiché dans la liste)</label>
          <input className="itm-input" value={name} onChange={e => setName(e.target.value)}
            placeholder="Ex: Passerelle Clinique X" />
        </div>

        <div className="form-group">
          <label className="itm-label">Identifiant du host (unifiHostId)</label>
          <input className="itm-input" value={hostId} onChange={e => setHostId(e.target.value)}
            placeholder="Rempli automatiquement en choisissant un site ci-dessus" />
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────

export default function MonitoringPage() {
  const { toasts, toast, removeToast } = useToast();

  const [nasList, setNasList]         = useState([]);
  const [gatewayList, setGatewayList] = useState([]);
  const [loading, setLoading]         = useState(true);

  const [syncingNas, setSyncingNas]           = useState(false);
  const [syncingGateways, setSyncingGateways] = useState(false);
  const [syncingNasId, setSyncingNasId]           = useState(null); // id du NAS en cours de sync individuelle
  const [syncingGatewayId, setSyncingGatewayId]   = useState(null);

  const [showAddNas, setShowAddNas]         = useState(false);
  const [showAddGateway, setShowAddGateway] = useState(false);
  const [editingNas, setEditingNas]         = useState(null); // NAS en cours d'édition, ou null
  const [editingGateway, setEditingGateway] = useState(null);
  const [backupFilter, setBackupFilter]     = useState("ALL"); // ALL | OK | WARNING | CRITICAL

  const load = async () => {
    try {
      const data = await getOverview();
      setNasList(data?.nas ?? []);
      setGatewayList(data?.gateways ?? []);
    } catch (err) {
      toast.error(err.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSyncNas = async () => {
    setSyncingNas(true);
    try {
      const res = await syncNasNow();
      toast.success(`NAS : ${res.synced} synchronisé(s), ${res.failed} échec(s) sur ${res.total}`);
      await load();
    } catch (err) {
      toast.error(err.message || "Erreur de synchronisation");
    } finally {
      setSyncingNas(false);
    }
  };

  const handleSyncGateways = async () => {
    setSyncingGateways(true);
    try {
      const res = await syncGatewaysNow();
      toast.success(`UniFi : ${res.synced} synchronisée(s), ${res.unmatched} non trouvée(s) sur ${res.total}`);
      await load();
    } catch (err) {
      toast.error(err.message || "Erreur de synchronisation");
    } finally {
      setSyncingGateways(false);
    }
  };

  const handleSyncOneNas = async (nas) => {
    setSyncingNasId(nas.id);
    try {
      await syncOneNas(nas.id);
      toast.success(`"${nas.name}" synchronisé`);
      await load();
    } catch (err) {
      toast.error(err.message || "Erreur de synchronisation");
      await load(); // recharge quand même : lastError a pu être sauvegardé côté serveur
    } finally {
      setSyncingNasId(null);
    }
  };

  const handleSyncOneGateway = async (gw) => {
    setSyncingGatewayId(gw.id);
    try {
      await syncOneGateway(gw.id);
      toast.success(`"${gw.name}" synchronisée`);
      await load();
    } catch (err) {
      toast.error(err.message || "Erreur de synchronisation");
      await load();
    } finally {
      setSyncingGatewayId(null);
    }
  };

  const handleDeleteNas = async (nas) => {
    if (!window.confirm(`Retirer "${nas.name}" de la supervision ?`)) return;
    try {
      await deleteNas(nas.id);
      toast.success("NAS retiré");
      await load();
    } catch (err) {
      toast.error(err.message || "Erreur");
    }
  };

  const handleDeleteGateway = async (gw) => {
    if (!window.confirm(`Retirer "${gw.name}" de la supervision ?`)) return;
    try {
      await deleteGateway(gw.id);
      toast.success("Passerelle retirée");
      await load();
    } catch (err) {
      toast.error(err.message || "Erreur");
    }
  };

  const counts = { OK: 0, WARNING: 0, CRITICAL: 0 };
  [...nasList, ...gatewayList].forEach(d => { if (counts[d.status] !== undefined) counts[d.status]++; });

  // Toutes les tâches de sauvegarde de tous les NAS, aplaties en une seule
  // liste — chaque NAS porte déjà son tableau backupJobs (JSONB rempli à
  // chaque sync), on ne fait que les regrouper avec le contexte client/NAS.
  const backupJobs = nasList.flatMap(n =>
    (n.backupJobs || []).map((j, i) => ({ ...j, _key: `${n.id}-${i}`, nasId: n.id, nasName: n.name, companyName: n.companyName }))
  );
  const backupCounts = { OK: 0, WARNING: 0, CRITICAL: 0 };
  backupJobs.forEach(j => { if (backupCounts[j.status] !== undefined) backupCounts[j.status]++; });
  const filteredBackupJobs = backupFilter === "ALL" ? backupJobs : backupJobs.filter(j => j.status === backupFilter);

  return (
    <>
      <Topbar title="Supervision" subtitle="NAS & UniFi" />
      <div className="page-body">

        {/* ── En-tête ── */}
        <div className="page-header">
          <div>
            <div className="page-title">Supervision <span>infrastructure clients</span></div>
            <div className="page-sub">NAS Synology et passerelles UniFi de tous les clients</div>
          </div>
          <div className="page-actions" style={{ display: "flex", gap: 10 }}>
            <button className="btn-itm btn-itm-outline" onClick={handleSyncNas} disabled={syncingNas || nasList.length === 0}>
              <i className={`bi bi-arrow-repeat ${syncingNas ? "spin" : ""} me-1`}/>
              {syncingNas ? "Sync NAS…" : "Synchroniser les NAS"}
            </button>
            <button className="btn-itm btn-itm-outline" onClick={handleSyncGateways} disabled={syncingGateways || gatewayList.length === 0}>
              <i className={`bi bi-arrow-repeat ${syncingGateways ? "spin" : ""} me-1`}/>
              {syncingGateways ? "Sync UniFi…" : "Synchroniser UniFi"}
            </button>
          </div>
        </div>

        {/* ── Métriques ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 20 }}>
          {[
            { icon: "bi-hdd-network",          color: "teal",   label: "Appareils supervisés", value: nasList.length + gatewayList.length },
            { icon: "bi-check-circle",         color: "green",  label: "OK",                   value: counts.OK },
            { icon: "bi-exclamation-triangle", color: "orange", label: "Avertissement",         value: counts.WARNING },
            { icon: "bi-x-octagon",            color: "purple", label: "Critique",              value: counts.CRITICAL },
          ].map((m, i) => (
            <div key={i} className={`metric-card mc-${m.color} anim-fade-up`} style={{ animationDelay: `${.05 + i * .05}s` }}>
              <div className="metric-glow" />
              <div className="metric-icon"><i className={`bi ${m.icon}`} /></div>
              <div className="metric-label">{m.label}</div>
              <div style={{ fontFamily: "Syne,sans-serif", fontSize: 22, fontWeight: 800, color: "var(--text-1)" }}>
                {m.value}
              </div>
            </div>
          ))}
        </div>

        {/* ── NAS Synology ── */}
        <div className="itm-card anim-fade-up" style={{ marginBottom: 20 }}>
          <div className="card-title-row">
            <div>
              <div className="card-title">NAS Synology</div>
              <div className="card-sub">{nasList.length} NAS enregistré(s)</div>
            </div>
            <button className="btn-itm btn-itm-primary" style={{ fontSize: 12, padding: "6px 14px" }}
              onClick={() => setShowAddNas(true)}>
              <i className="bi bi-plus-lg me-1"/>Ajouter un NAS
            </button>
          </div>

          {loading ? (
            <div style={{ padding: "30px 0", textAlign: "center", color: "var(--text-3)" }}>Chargement…</div>
          ) : nasList.length === 0 ? (
            <EmptyState icon="bi-hdd-network" message="Aucun NAS enregistré." />
          ) : (
            <table className="itm-table">
              <thead>
                <tr>
                  <th>Statut</th><th>Nom</th><th>Entreprise</th><th>Stockage</th>
                  <th>RAID</th><th>Dernière sync</th><th>Erreur</th><th></th>
                </tr>
              </thead>
              <tbody>
                {nasList.map(n => (
                  <tr key={n.id}>
                    <td><HealthBadge status={n.status} /></td>
                    <td style={{ fontWeight: 600, color: "var(--text-1)" }}>{n.name}</td>
                    <td style={{ fontSize: 12, color: "var(--text-2)" }}>{n.companyName}</td>
                    <td style={{ fontSize: 12, color: "var(--text-2)" }}>
                      {n.storagePercent != null ? `${n.storagePercent}%` : "—"}
                    </td>
                    <td style={{ fontSize: 12, color: "var(--text-2)" }}>{n.raidStatus || "—"}</td>
                    <td style={{ fontSize: 12, color: "var(--text-3)" }}>{timeAgo(n.lastSyncedAt)}</td>
                    <td style={{ fontSize: 12, color: n.lastError ? "var(--danger)" : "var(--text-3)" }}>
                      {n.lastError || "—"}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button className="btn-itm btn-itm-outline" style={{ padding: "4px 8px", fontSize: 11 }}
                          title="Synchroniser ce NAS" onClick={() => handleSyncOneNas(n)} disabled={syncingNasId === n.id}>
                          <i className={`bi bi-arrow-repeat ${syncingNasId === n.id ? "spin" : ""}`}/>
                        </button>
                        <button className="btn-itm btn-itm-outline" style={{ padding: "4px 8px", fontSize: 11 }}
                          title="Modifier" onClick={() => setEditingNas(n)}>
                          <i className="bi bi-pencil"/>
                        </button>
                        <button className="btn-itm btn-itm-outline" style={{ padding: "4px 8px", fontSize: 11, color: "var(--danger)", borderColor: "rgba(239,68,68,.3)" }}
                          title="Supprimer" onClick={() => handleDeleteNas(n)}>
                          <i className="bi bi-trash"/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Sauvegardes (toutes tâches, tous NAS) ── */}
        <div className="itm-card anim-fade-up delay-1" style={{ marginBottom: 20 }}>
          <div className="card-title-row">
            <div>
              <div className="card-title">Sauvegardes</div>
              <div className="card-sub">{backupJobs.length} tâche(s) détectée(s) sur {nasList.length} NAS</div>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                { key: "ALL",      label: "Toutes" },
                { key: "OK",       label: `Succès (${backupCounts.OK})` },
                { key: "WARNING",  label: `Avertissement (${backupCounts.WARNING})` },
                { key: "CRITICAL", label: `Échec (${backupCounts.CRITICAL})` },
              ].map(f => (
                <button key={f.key}
                  className="btn-itm btn-itm-outline"
                  style={{
                    padding: "4px 10px", fontSize: 11,
                    borderColor: backupFilter === f.key ? "var(--accent)" : undefined,
                    background: backupFilter === f.key ? "var(--accent-dim)" : undefined,
                  }}
                  onClick={() => setBackupFilter(f.key)}
                >{f.label}</button>
              ))}
            </div>
          </div>

          {loading ? (
            <div style={{ padding: "30px 0", textAlign: "center", color: "var(--text-3)" }}>Chargement…</div>
          ) : backupJobs.length === 0 ? (
            <EmptyState icon="bi-cloud-arrow-up"
              message="Aucune tâche de sauvegarde détectée pour l'instant — synchronise les NAS (Hyper Backup ou Active Backup for Business) pour les récupérer." />
          ) : filteredBackupJobs.length === 0 ? (
            <EmptyState icon="bi-cloud-arrow-up" message="Aucune tâche dans cette catégorie." />
          ) : (
            <table className="itm-table">
              <thead>
                <tr>
                  <th>Statut</th><th>Entreprise</th><th>NAS</th><th>Type</th><th>Tâche</th>
                  <th>Dernière exécution</th><th>Prochaine exécution</th><th>Durée</th><th>Résultat DSM</th>
                </tr>
              </thead>
              <tbody>
                {filteredBackupJobs.map(j => (
                  <tr key={j._key}>
                    <td><HealthBadge status={j.status} /></td>
                    <td style={{ fontSize: 12, color: "var(--text-2)" }}>{j.companyName}</td>
                    <td style={{ fontSize: 12, color: "var(--text-2)" }}>{j.nasName}</td>
                    <td style={{ fontSize: 12, color: "var(--text-2)" }}>{j.source || "—"}</td>
                    <td style={{ fontWeight: 600, color: "var(--text-1)" }}>{j.jobName}</td>
                    <td style={{ fontSize: 12, color: "var(--text-3)" }}>{j.lastRunLabel || "—"}</td>
                    <td style={{ fontSize: 12, color: "var(--text-3)" }}>{j.nextRunLabel || "—"}</td>
                    <td style={{ fontSize: 12, color: "var(--text-3)" }}>{j.durationLabel || "—"}</td>
                    <td style={{ fontSize: 12, color: "var(--text-3)" }}>{j.rawStatus || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Passerelles UniFi ── */}
        <div className="itm-card anim-fade-up delay-2">
          <div className="card-title-row">
            <div>
              <div className="card-title">Passerelles UniFi</div>
              <div className="card-sub">{gatewayList.length} passerelle(s) enregistrée(s)</div>
            </div>
            <button className="btn-itm btn-itm-primary" style={{ fontSize: 12, padding: "6px 14px" }}
              onClick={() => setShowAddGateway(true)}>
              <i className="bi bi-plus-lg me-1"/>Ajouter une passerelle
            </button>
          </div>

          {loading ? (
            <div style={{ padding: "30px 0", textAlign: "center", color: "var(--text-3)" }}>Chargement…</div>
          ) : gatewayList.length === 0 ? (
            <EmptyState icon="bi-router" message="Aucune passerelle enregistrée." />
          ) : (
            <table className="itm-table">
              <thead>
                <tr>
                  <th>Statut</th><th>Nom</th><th>Entreprise</th><th>WAN</th>
                  <th>Latence</th><th>Débit</th><th>Dernière sync</th><th>Erreur</th><th></th>
                </tr>
              </thead>
              <tbody>
                {gatewayList.map(g => (
                  <tr key={g.id}>
                    <td><HealthBadge status={g.status} /></td>
                    <td style={{ fontWeight: 600, color: "var(--text-1)" }}>{g.name}</td>
                    <td style={{ fontSize: 12, color: "var(--text-2)" }}>{g.companyName}</td>
                    <td style={{ fontSize: 12, color: "var(--text-2)" }}>{g.wanStatus || "—"}</td>
                    <td style={{ fontSize: 12, color: "var(--text-2)" }}>
                      {g.latencyMs != null ? `${g.latencyMs} ms` : "—"}
                    </td>
                    <td style={{ fontSize: 12, color: "var(--text-2)" }}>
                      {g.downMbps != null ? `↓${g.downMbps} / ↑${g.upMbps} Mbps` : "—"}
                    </td>
                    <td style={{ fontSize: 12, color: "var(--text-3)" }}>{timeAgo(g.lastSyncedAt)}</td>
                    <td style={{ fontSize: 12, color: g.lastError ? "var(--danger)" : "var(--text-3)" }}>
                      {g.lastError || "—"}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button className="btn-itm btn-itm-outline" style={{ padding: "4px 8px", fontSize: 11 }}
                          title="Synchroniser cette passerelle" onClick={() => handleSyncOneGateway(g)} disabled={syncingGatewayId === g.id}>
                          <i className={`bi bi-arrow-repeat ${syncingGatewayId === g.id ? "spin" : ""}`}/>
                        </button>
                        <button className="btn-itm btn-itm-outline" style={{ padding: "4px 8px", fontSize: 11 }}
                          title="Modifier" onClick={() => setEditingGateway(g)}>
                          <i className="bi bi-pencil"/>
                        </button>
                        <button className="btn-itm btn-itm-outline" style={{ padding: "4px 8px", fontSize: 11, color: "var(--danger)", borderColor: "rgba(239,68,68,.3)" }}
                          title="Supprimer" onClick={() => handleDeleteGateway(g)}>
                          <i className="bi bi-trash"/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showAddNas && (
          <AddNasModal
            toast={toast}
            onClose={() => setShowAddNas(false)}
            onSaved={() => { setShowAddNas(false); load(); }}
          />
        )}
        {editingNas && (
          <AddNasModal
            editing={editingNas}
            toast={toast}
            onClose={() => setEditingNas(null)}
            onSaved={() => { setEditingNas(null); load(); }}
          />
        )}
        {showAddGateway && (
          <AddGatewayModal
            toast={toast}
            onClose={() => setShowAddGateway(false)}
            onSaved={() => { setShowAddGateway(false); load(); }}
          />
        )}
        {editingGateway && (
          <AddGatewayModal
            editing={editingGateway}
            toast={toast}
            onClose={() => setEditingGateway(null)}
            onSaved={() => { setEditingGateway(null); load(); }}
          />
        )}

        <ToastContainer toasts={toasts} onRemove={removeToast} />

        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          .spin { animation: spin .7s linear infinite; display: inline-block; }
        `}</style>
      </div>
    </>
  );
}